import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { REVIEWS, REVIEW_SUMMARY } from "@/data/reviews";

/** Multicoloured Google "G" logo. */
function GoogleG({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/**
 * Small live review ticker used inside the Hero section — rotates through
 * short customer quotes every ~5s with a subtle fade transition.
 */
export default function MiniReviewTicker() {
  const [i, setI] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const cycle = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setI((prev) => (prev + 1) % REVIEWS.length);
        setVisible(true);
      }, 320); // must match CSS fade duration
    }, 4600);
    return () => clearInterval(cycle);
  }, []);

  const r = REVIEWS[i];
  // Truncate long quotes to keep the strip slim
  const quote = r.text.length > 82 ? `${r.text.slice(0, 82).trim()}…` : r.text;

  return (
    <a
      data-testid="mini-review-ticker"
      href={REVIEW_SUMMARY.googleUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`See our ${REVIEW_SUMMARY.count} Google reviews`}
      className="mini-review inline-flex items-center gap-2 sm:gap-3 max-w-full rounded-full bg-white/95 backdrop-blur border border-black/10 shadow-[0_6px_20px_-8px_rgba(0,0,0,0.25)] pl-2 pr-3.5 py-1.5 hover:border-[#1E63FF]/50 transition-colors"
    >
      {/* Rating chip */}
      <span className="shrink-0 inline-flex items-center gap-1 bg-[#0A0A0A] text-white rounded-full px-2 py-1">
        <GoogleG className="w-3 h-3" />
        <span className="text-[11px] font-bold leading-none">{REVIEW_SUMMARY.rating.toFixed(1)}</span>
        <span className="inline-flex items-center gap-[1px]">
          {Array.from({ length: 5 }).map((_, s) => (
            <Star key={s} className="w-2.5 h-2.5 fill-[#FBBC04] stroke-[#FBBC04]" strokeWidth={1.5} />
          ))}
        </span>
      </span>

      {/* Rotating quote */}
      <span
        data-testid="mini-review-quote"
        className={`text-[12px] sm:text-[13px] text-[#0A0A0A] font-medium truncate transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
      >
        <span className="text-neutral-500">"</span>
        {quote}
        <span className="text-neutral-500">"</span>
        <span className="hidden sm:inline text-neutral-500 font-normal"> — {r.name.split(" ")[0]}</span>
      </span>

      {/* Live pulse dot */}
      <span className="hidden sm:flex shrink-0 relative h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0F9D58] opacity-75"></span>
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#0F9D58]"></span>
      </span>
    </a>
  );
}
